/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { theme } from "@nordcloud/gnui";
import anomalyCostsIndividualEmail from "~/assets/images/workflows/anomalyCostsIndividualEmail.png";
import anomalyCostsOwnerEmail from "~/assets/images/workflows/anomalyCostsOwnerEmail.png";
import budgetsIndividualEmail from "~/assets/images/workflows/budgetsIndividualEmail.png";
import budgetsOwnerEmail from "~/assets/images/workflows/budgetsOwnerEmail.png";
import cloudCredentialsEmail from "~/assets/images/workflows/cloudCredentialsEmail.png";
import costsCombinedEmail from "~/assets/images/workflows/costsCombinedEmail.png";
import costsIndividualEmail from "~/assets/images/workflows/costsIndividualEmail.png";
import costsOwnerEmail from "~/assets/images/workflows/costsOwnerEmail.png";
import kpisEmail from "~/assets/images/workflows/kpisEmail.png";
import savingSuggestionsCombinedEmail from "~/assets/images/workflows/savingSuggestionsCombinedEmail.png";
import savingSuggestionsIndividualEmail from "~/assets/images/workflows/savingSuggestionsIndividualEmail.png";
import savingSuggestionsOwnerEmail from "~/assets/images/workflows/savingSuggestionsOwnerEmail.png";
import unallocatedResourcesEmail from "~/assets/images/workflows/unallocatedResourcesEmail.png";
import { PathName } from "~/tools";

export const enum ComponentGroup {
  TRIGGER = "TRIGGER",
  DATA = "DATA",
  OUTPUT = "OUTPUT",
}

export enum WorkflowDataType {
  BUDGETS = "BUDGETS",
  COSTS = "COSTS",
  KPI = "KPI",
  SAVING_SUGGESTIONS = "SAVING_SUGGESTIONS",
  UNALLOCATED_RESOURCES = "UNALLOCATED_RESOURCES",
  ANOMALY_COSTS = "ANOMALY_COSTS",
  CLOUD_CREDENTIALS = "CLOUD_CREDENTIALS",
}

enum WorkflowTriggerType {
  MANUAL = "MANUAL",
  SCAN = "SCAN",
  SCHEDULER = "SCHEDULER",
}

enum WorkflowExternalIntegrationType {
  EMAIL = "EMAIL",
  SERVICE_NOW = "SERVICE_NOW",
  SLACK = "SLACK",
}

export type ComponentType =
  | `${WorkflowDataType}`
  | `${WorkflowExternalIntegrationType}`
  | `${WorkflowTriggerType}`;

export const ComponentGroupColorMap = {
  [ComponentGroup.TRIGGER]: theme.color.support.lightGreen,
  [ComponentGroup.DATA]: theme.color.support.cyan,
  [ComponentGroup.OUTPUT]: theme.color.support.orange,
};

export type ComponentConfig = {
  title: string;
  group: ComponentGroup;
  type: ComponentType;
  color: string;
  iconName: PathName;
  description: string;
};

export const SchedulerConfig: ComponentConfig = {
  group: ComponentGroup.TRIGGER,
  type: WorkflowTriggerType.SCHEDULER,
  title: "Scheduler",
  color: ComponentGroupColorMap[ComponentGroup.TRIGGER],
  iconName: "eventSchedule",
  description:
    "The Scheduler component is a trigger-type component of the workflow, used to set up a run of the workflow at fixed times, dates, or intervals.",
};

export const ScanConfig: ComponentConfig = {
  group: ComponentGroup.TRIGGER,
  type: WorkflowTriggerType.SCAN,
  title: "Scan Completed",
  color: ComponentGroupColorMap[ComponentGroup.TRIGGER],
  iconName: "zoomArea",
  description:
    "The Scan Completed is a trigger-type component of the workflow, used to trigger workflow once daily scanning of connected accounts is completed.",
};

export const BudgetsConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.BUDGETS,
  title: "Budgets",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "budget",
  description:
    "The Budget component is a send data type component used to select budgets, costs, and forecast costs, for the current month, for select applications, environments, and Organizational Units.",
};

export const CostsConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.COSTS,
  title: "Costs",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "costs",
  description:
    "The Costs component is a select data type component used to select application costs.",
};

export const KPIConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.KPI,
  title: "KPIs",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "chartAscending",
  description:
    "The KPIs component is a select data type component used to select values and targets of the KPIs.",
};

export const NewUnallocatedResourcesConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.UNALLOCATED_RESOURCES,
  title: "New Unallocated Resources",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "resource",
  description: "",
};

export const SavingsSuggestionsConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.SAVING_SUGGESTIONS,
  title: "Savings Suggestions",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "cloudWaste",
  description:
    "The Savings Suggestions component is a select data type component used to select savings suggestions.",
};

export const AnomalyCostsConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.ANOMALY_COSTS,
  title: "Anomaly Costs",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "costs", // Change to proper icon when its added to GNUI
  description:
    "The Anomaly Costs component is a select data type component used to select applications.",
};

export const CloudCredentialsConfig: ComponentConfig = {
  group: ComponentGroup.DATA,
  type: WorkflowDataType.CLOUD_CREDENTIALS,
  title: "Cloud Credentials",
  color: ComponentGroupColorMap[ComponentGroup.DATA],
  iconName: "cloudAccounts",
  description: "",
};

export const ServiceNowConfig: ComponentConfig = {
  group: ComponentGroup.OUTPUT,
  type: WorkflowExternalIntegrationType.SERVICE_NOW,
  title: "ServiceNow",
  color: ComponentGroupColorMap[ComponentGroup.OUTPUT],
  iconName: "serviceNow",
  description:
    "The ServiceNow component is a send data-type component of the workflow, used to send data in the incident on the ServiceNow instance.",
};

export const EmailConfig: ComponentConfig = {
  group: ComponentGroup.OUTPUT,
  type: WorkflowExternalIntegrationType.EMAIL,
  title: "Email",
  color: ComponentGroupColorMap[ComponentGroup.OUTPUT],
  iconName: "mail",
  description:
    "The Email component is a send data-type component of the workflow, used to send data via email.",
};

export const SlackConfig: ComponentConfig = {
  group: ComponentGroup.OUTPUT,
  type: WorkflowExternalIntegrationType.SLACK,
  title: "Slack",
  color: ComponentGroupColorMap[ComponentGroup.OUTPUT],
  iconName: "slack",
  description:
    "The Slack component is a send data-type component of the workflow used to create a message in a Slack channel.",
};

export const enum WorkflowSidebars {
  EMAIL = "email",
  SERVICENOW = "serviceNow",
  SCHEDULER = "scheduler",
  SLACK = "slack",
  SAVINGS_SUGGESTIONS = "savingsSuggestions",
  KPI = "kpi",
  COSTS = "costs",
  ANOMALY_COSTS = "anomalyCosts",
  BUDGETS = "budgets",
  NONE = "none",
}

export const ComponentConfigMap: Record<
  WorkflowExternalIntegrationType,
  ComponentConfig
> = {
  EMAIL: EmailConfig,
  SERVICE_NOW: ServiceNowConfig,
  SLACK: SlackConfig,
};

export enum EventStatus {
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
}

export enum LastRunStatus {
  FAILED = "FAILED",
  COMPLETED = "COMPLETED",
  NONE = "NONE",
}

type PreviewMessageMap = Record<WorkflowDataType, string>;

export const previewMessage: PreviewMessageMap = {
  [WorkflowDataType.BUDGETS]: budgetsIndividualEmail,
  [WorkflowDataType.UNALLOCATED_RESOURCES]: unallocatedResourcesEmail,
  [WorkflowDataType.KPI]: kpisEmail,
  [WorkflowDataType.ANOMALY_COSTS]: anomalyCostsIndividualEmail,
  [WorkflowDataType.COSTS]: "",
  [WorkflowDataType.SAVING_SUGGESTIONS]: "",
  [WorkflowDataType.CLOUD_CREDENTIALS]: cloudCredentialsEmail,
};

export const ownerPreviewMessage: PreviewMessageMap = {
  [WorkflowDataType.BUDGETS]: budgetsOwnerEmail,
  [WorkflowDataType.KPI]: kpisEmail,
  [WorkflowDataType.ANOMALY_COSTS]: anomalyCostsOwnerEmail,
  [WorkflowDataType.COSTS]: costsOwnerEmail,
  [WorkflowDataType.SAVING_SUGGESTIONS]: savingSuggestionsOwnerEmail,
  [WorkflowDataType.UNALLOCATED_RESOURCES]: "",
  [WorkflowDataType.CLOUD_CREDENTIALS]: "",
};

export const individualPreviewImage: PreviewMessageMap = {
  [WorkflowDataType.KPI]: kpisEmail,
  [WorkflowDataType.COSTS]: costsIndividualEmail,
  [WorkflowDataType.SAVING_SUGGESTIONS]: savingSuggestionsIndividualEmail,
  [WorkflowDataType.ANOMALY_COSTS]: "",
  [WorkflowDataType.BUDGETS]: "",
  [WorkflowDataType.UNALLOCATED_RESOURCES]: "",
  [WorkflowDataType.CLOUD_CREDENTIALS]: "",
};

export const combinedPreviewImage: PreviewMessageMap = {
  [WorkflowDataType.COSTS]: costsCombinedEmail,
  [WorkflowDataType.SAVING_SUGGESTIONS]: savingSuggestionsCombinedEmail,
  [WorkflowDataType.ANOMALY_COSTS]: "",
  [WorkflowDataType.BUDGETS]: "",
  [WorkflowDataType.KPI]: "",
  [WorkflowDataType.UNALLOCATED_RESOURCES]: "",
  [WorkflowDataType.CLOUD_CREDENTIALS]: "",
};
